import {
	Box,
	IconButton,
	Typography,
	Select,
	MenuItem,
	Button,
	useTheme,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import type {
	Block,
	Segment as SegmentType,
} from '../../../../../../../../types/shared/blocksV3';
import EdgeType from '../../../../../../../../enums/edgeType';
import BlockType from '../../../../../../../../enums/blockType';
import {
	BumpoutEdgeTypes,
	DeckBoxEdgeTypes,
	DeckContext,
	LandingEdgeTypes,
	StairEdgeTypes,
	updateBlock,
} from '../../../../context/DeckContextV3';
import FeetInchesInput from '../../../FeetInchesInputV2';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { Modal } from '../../../../../../../../globalComponents/modal/Modal';
import swapSegmentsBlocksPositions from '../../../../../../../../utils/swapSegmentsBlocksPositions';
import getBlockChildren from '../../../../../../../../utils/getBlockChildrenV3';
import { getOppositeEdge } from '../../../../utils/edgeUtils';
import {
	MIN_STAIR_VIABLE_BLOCK_HEIGHT,
	MIN_STAIR_WIDTH,
	MIN_LANDING_WIDTH,
} from '../../../../../../../../constants/limits';
import getVacantSegment, {
	getVacantSegmentWithoutValidation,
} from '../../../../utils/getVacantSegment';
import {
	getBlockUpdatedPosition,
	getEdgeChildrenUpdatedPositions,
} from '../../../../utils/getUpdatedBlockPosition';

const Segment: React.FC<{
	key: any;
	segment: SegmentType;
	segmentIndex: number;
	block: Block;
	segmentsCount: number;
	setError: Dispatch<SetStateAction<string | null>>;
	clickAwayLockedRef: React.RefObject<boolean>;
}> = ({
	segment,
	segmentIndex,
	block,
	segmentsCount,
	setError,
	clickAwayLockedRef,
}) => {
	const { state, dispatch } = useContext(DeckContext);
	const theme = useTheme();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [reinitialize, setReinitialize] = useState(0);

	const parentEdge = block.edges.find((edge) => edge.id == segment.edgeId);

	const blockChildren = getBlockChildren(state.blocks, block);

	const childBlockAttachedToSegment = blockChildren.find(
		(child) => child.parentBlock.segmentId == segment.id
	);

	const handleLengthChange = (inches: number) => {
		const updatedBlock = { ...block };

		let updatedChildBlock: Block = { ...childBlockAttachedToSegment };

		if (
			childBlockAttachedToSegment &&
			childBlockAttachedToSegment.type != BlockType.DECKBOX &&
			parentEdge
		) {
			const displayWidthDimension =
				parentEdge.label == 'Top' || parentEdge.label == 'Bottom'
					? 'width'
					: 'depth';

			const vacantSegment = getVacantSegmentWithoutValidation({
				blocks: state.blocks,
				selectedBlockEdge: parentEdge,
			});

			if (inches < segment.length) {
				const minWidth =
					childBlockAttachedToSegment.type === BlockType.LANDING
						? MIN_LANDING_WIDTH
						: MIN_STAIR_WIDTH;

				if (inches < minWidth) {
					setError(
						`Segment ${segmentIndex + 1} length cannot be less than min ${childBlockAttachedToSegment.type} width: ${minWidth / 12}ft`
					);

					setReinitialize(Date.now());

					return;
				} else {
					setError(null);

					// On DM-837-stg, width maps strictly to X-axis and depth strictly to Y-axis.
					// So Top/Bottom segments influence width (X-axis), Left/Right influence depth (Y-axis).
					const childAttachedEdge = updatedChildBlock.edges?.find(
						(e) => e.attachedToParent
					);
					const childIsTopBottom =
						childAttachedEdge?.label === 'Top' ||
						childAttachedEdge?.label === 'Bottom';

					const newChildWidth = childIsTopBottom
						? inches
						: updatedChildBlock.dimensions.width;
					const newChildDepth = childIsTopBottom
						? updatedChildBlock.dimensions.depth
						: inches;

					updatedChildBlock = {
						...updatedChildBlock,
						dimensions: {
							...updatedChildBlock.dimensions,
							width: newChildWidth,
							depth: newChildDepth,
						},
						displayDimensions: {
							...updatedChildBlock.displayDimensions,
							width: newChildWidth,
							height: newChildDepth,
						},
						edges: updatedChildBlock.edges.map((edge) => {
							const isWidthEdge =
								edge.label === 'Top' || edge.label === 'Bottom';
							const updatedLength = isWidthEdge ? newChildWidth : newChildDepth;

							return {
								...edge,
								length: updatedLength,
								segments: edge.segments.map((seg) => ({
									...seg,
									length: updatedLength,
								})),
							};
						}),
					};

					// Build updated parent edges FIRST so position is computed
					// against the new segment layout (not stale pre-resize state)
					updatedBlock.edges = block?.edges.map((edge) => {
						if (edge.id == segment.edgeId) {
							const updatedEdge = { ...edge };

							updatedEdge.segments = updatedEdge.segments.map((seg) => {
								if (seg.id == segment.id) {
									return { ...seg, length: inches };
								} else if (seg.id == vacantSegment?.id) {
									return {
										...seg,
										length: seg.length + (segment.length - inches),
									};
								}

								return seg;
							});

							return updatedEdge;
						} else {
							return edge;
						}
					});

					// Use updatedBlock (with new segment layout) as parent for position
					const newPosition = getBlockUpdatedPosition(
						updatedBlock,
						updatedChildBlock,
						state.scaleFactor
					);
					updatedChildBlock.position = newPosition;
				}
			} else if (inches > segment.length) {
				const segmentsLength = parentEdge.segments.reduce(
					(acc, segment) => acc + segment.length,
					0
				);

				const availableLength =
					(vacantSegment?.length || 0) + (parentEdge.length - segmentsLength);

				if (availableLength < inches - segment.length) {
					setError(
						`Edge ${parentEdge?.label} has no space to expand segment ${segmentIndex + 1} to ${inches / 12}ft `
					);

					setReinitialize(Date.now());

					return;
				} else {
					setError(null);

					// On DM-837-stg, width maps strictly to X-axis and depth strictly to Y-axis.
					// So Top/Bottom segments influence width (X-axis), Left/Right influence depth (Y-axis).
					const childAttachedEdge = updatedChildBlock.edges?.find(
						(e) => e.attachedToParent
					);
					const childIsTopBottom =
						childAttachedEdge?.label === 'Top' ||
						childAttachedEdge?.label === 'Bottom';

					const newChildWidth = childIsTopBottom
						? inches
						: updatedChildBlock.dimensions.width;
					const newChildDepth = childIsTopBottom
						? updatedChildBlock.dimensions.depth
						: inches;

					updatedChildBlock = {
						...updatedChildBlock,
						dimensions: {
							...updatedChildBlock.dimensions,
							width: newChildWidth,
							depth: newChildDepth,
						},
						displayDimensions: {
							...updatedChildBlock.displayDimensions,
							width: newChildWidth,
							height: newChildDepth,
						},
						edges: updatedChildBlock.edges.map((edge) => {
							const isWidthEdge =
								edge.label === 'Top' || edge.label === 'Bottom';
							const updatedLength = isWidthEdge ? newChildWidth : newChildDepth;

							return {
								...edge,
								length: updatedLength,
								segments: edge.segments.map((seg) => ({
									...seg,
									length: updatedLength,
								})),
							};
						}),
					};

					// Build updated parent edges FIRST so position is computed
					// against the new segment layout (not stale pre-resize state)
					updatedBlock.edges = block?.edges.map((edge) => {
						if (edge.id == segment.edgeId) {
							const updatedEdge = { ...edge };

							updatedEdge.segments = updatedEdge.segments.map((seg) => {
								if (seg.id == segment.id) {
									return { ...seg, length: inches };
								} else if (seg.id == vacantSegment?.id) {
									return {
										...seg,
										length: Math.max(0, seg.length - (inches - segment.length)),
									};
								}

								return seg;
							});

							return updatedEdge;
						} else {
							return edge;
						}
					});

					// Use updatedBlock (with new segment layout) as parent for position
					const newPosition = getBlockUpdatedPosition(
						updatedBlock,
						updatedChildBlock,
						state.scaleFactor
					);
					updatedChildBlock.position = newPosition;
				}
			}
		} else {
			setError(null);

			updatedBlock.edges = block?.edges.map((edge) => {
				if (edge.id == segment.edgeId) {
					const updatedEdge = { ...edge };

					updatedEdge.segments = updatedEdge.segments.map((seg) => {
						if (seg.id == segment.id) {
							return { ...seg, length: inches };
						}

						return seg;
					});

					return updatedEdge;
				} else {
					return edge;
				}
			});

			const isAttachedType =
				segment.type === EdgeType.STAIR_ATTACHED ||
				segment.type === EdgeType.LANDING_ATTACHED;
			if (isAttachedType && inches >= MIN_STAIR_WIDTH) {
				const updatedSegment = { ...segment, length: inches };
				dispatch({
					type: 'CHANGE_EDGE_TYPE',
					payload: { segment: updatedSegment, type: segment.type },
				});
				return;
			}
		}

		let updatedBlocks;

		const result = updateBlock(state, updatedBlock);
		updatedBlocks = result.blocks;

		const result2 = updateBlock(
			{ ...state, blocks: updatedBlocks },
			updatedChildBlock
		);
		updatedBlocks = result2.blocks;

		updatedBlocks = getEdgeChildrenUpdatedPositions(
			updatedBlocks,
			block.id,
			parentEdge?.id,
			state.scaleFactor
		);

		dispatch({ type: 'UPDATE_BLOCKS', payload: updatedBlocks });
	};

	const handleEdgeChange = (type: EdgeType) => {
		dispatch({ type: 'CHANGE_EDGE_TYPE', payload: { segment, type } });
	};

	const handleDeleteSegment = () => {
		dispatch({
			type: 'DELETE_SEGMENT',
			payload: { block, edgeId: segment.edgeId, segmentId: segment.id },
		});
	};

	const moveSegmentUp = () => {
		let currentSegment;
		let prevSegment;

		block.edges = block?.edges.map((edge) => {
			if (edge.id == segment.edgeId) {
				const updatedEdge = { ...edge };

				const segmentIndex = updatedEdge.segments.findIndex(
					(seg) => seg.id == segment.id
				);

				currentSegment = updatedEdge.segments[segmentIndex];
				prevSegment = updatedEdge.segments[segmentIndex - 1];

				updatedEdge.segments[segmentIndex] = prevSegment;
				updatedEdge.segments[segmentIndex - 1] = currentSegment;

				return updatedEdge;
			} else {
				return edge;
			}
		});

		dispatch({ type: 'UPDATE_BLOCK', payload: block });

		swapSegmentsBlocksPositions({
			blocks: state.blocks,
			block,
			topSegment: prevSegment,
			bottomSegment: currentSegment,
			scaleFactor: state.scaleFactor,
			dispatch,
		});
	};

	const moveSegmentDown = () => {
		let currentSegment;
		let nextSegment;

		block.edges = block?.edges.map((edge) => {
			if (edge.id == segment.edgeId) {
				const updatedEdge = { ...edge };

				const segmentIndex = updatedEdge.segments.findIndex(
					(seg) => seg.id == segment.id
				);

				currentSegment = updatedEdge.segments[segmentIndex];
				nextSegment = updatedEdge.segments[segmentIndex + 1];

				updatedEdge.segments[segmentIndex] = nextSegment;
				updatedEdge.segments[segmentIndex + 1] = currentSegment;

				return updatedEdge;
			} else {
				return edge;
			}
		});

		dispatch({ type: 'UPDATE_BLOCK', payload: block });

		swapSegmentsBlocksPositions({
			blocks: state.blocks,
			block,
			topSegment: currentSegment,
			bottomSegment: nextSegment,
			scaleFactor: state.scaleFactor,
			dispatch,
		});
	};

	const heightDependentEdgeTypes = [
		EdgeType.STAIR_ATTACHED,
		EdgeType.LANDING_ATTACHED,
	];

	let edgeTypes: EdgeType[] = [];

	const parentAttachedEdge = block?.edges.find((edge) => edge.attachedToParent);

	const sideEdgeLabels = block?.edges
		?.filter(
			(edge) =>
				edge.label != parentAttachedEdge?.label &&
				edge.label != getOppositeEdge(parentAttachedEdge?.label)
		)
		.map((edge) => edge.label);

	if (
		block.type === BlockType.STAIR &&
		sideEdgeLabels?.includes(parentEdge?.label)
	) {
		edgeTypes = [EdgeType.RAILING, EdgeType.NO_RAILING];
	}

	switch (block.type) {
		case BlockType.DECKBOX:
			edgeTypes = DeckBoxEdgeTypes;
			break;
		case BlockType.BUMPOUT:
			edgeTypes = BumpoutEdgeTypes;
			break;
		case BlockType.STAIR:
			if (sideEdgeLabels?.includes(parentEdge?.label)) {
				edgeTypes = [EdgeType.RAILING, EdgeType.NO_RAILING];
			} else {
				edgeTypes = StairEdgeTypes;
			}
			break;
		case BlockType.LANDING:
			edgeTypes = LandingEdgeTypes;
			break;
		default:
			edgeTypes = [];
	}

	let showActionButtons = true;

	if (block?.type == BlockType.STAIR) {
		showActionButtons = !sideEdgeLabels?.includes(parentEdge?.label);
	}

	return (
		<Box
			sx={{
				width: 'calc(100% - 10px)',
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'start',
				gap: '5px',
				marginX: '5px',
				paddingY: '10px',
				borderBottom:
					segmentIndex < segmentsCount - 1
						? '1px solid rgba(0, 0, 0, 0.2)'
						: '',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					gap: '10px',
				}}
			>
				<IconButton disabled={segmentIndex == 0} onClick={moveSegmentUp}>
					<NorthIcon sx={{ fontSize: '20px' }} />
				</IconButton>

				<IconButton
					disabled={segmentsCount - 1 == segmentIndex}
					onClick={moveSegmentDown}
				>
					<SouthIcon sx={{ fontSize: '20px' }} />
				</IconButton>
			</Box>

			<Box sx={{}}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography>Segment {segmentIndex + 1}</Typography>

					<IconButton
						disabled={segmentsCount == 1}
						onClick={() => setShowDeleteModal(true)}
					>
						<DeleteIcon sx={{ color: segmentsCount > 1 ? 'red' : '' }} />
					</IconButton>
				</Box>

				<Select
					labelId="edge-select-label"
					variant="outlined"
					size="small"
					fullWidth
					value={
						segment.type === EdgeType.LANDING_ATTACHED
							? EdgeType.LANDING
							: segment.type === EdgeType.STAIR_ATTACHED
								? EdgeType.STAIR
								: segment.type
					}
					sx={{ marginBottom: '15px' }}
					onOpen={() => {
						clickAwayLockedRef.current = true;
					}}
					onClose={() => {
						setTimeout(() => {
							clickAwayLockedRef.current = false;
						}, 200);
					}}
					onChange={(e) => {
						let type = e.target.value as EdgeType;
						if (type === EdgeType.LANDING) {
							type = EdgeType.LANDING_ATTACHED;
						} else if (type === EdgeType.STAIR) {
							type = EdgeType.STAIR_ATTACHED;
						}
						handleEdgeChange(type);
					}}
					renderValue={(selected) => selected}
				>
					{edgeTypes
						.filter((edge) => {
							if (edge === EdgeType.BUMPOUT) {
								return (
									parentEdge?.type === EdgeType.HOUSE ||
									parentEdge?.type === EdgeType.HOUSE_ATTACHED ||
									segment.type === EdgeType.HOUSE ||
									segment.type === EdgeType.HOUSE_ATTACHED
								);
							}
							if (
								edge === EdgeType.LANDING_ATTACHED ||
								edge === EdgeType.STAIR_ATTACHED
							) {
								return false;
							}
							return true;
						})
						.map((edge) => {
							let label = edge;
							if (
								[
									EdgeType.BUMPOUT,
									EdgeType.DECKBOX,
									EdgeType.LANDING,
									EdgeType.STAIR,
									EdgeType.RAILING,
								].includes(edge)
							) {
								label = `Add ${edge}`;
							}
							return { value: edge, label };
						})
						.sort((a, b) => a.label.localeCompare(b.label))
						.map((option, index) => {
							const disbaled =
								(!showActionButtons &&
									[
										EdgeType.BLOCK_ATTACHED,
										EdgeType.STAIR_ATTACHED,
										EdgeType.LANDING_ATTACHED,
									].includes(option.value)) ||
								(block.dimensions.height < MIN_STAIR_VIABLE_BLOCK_HEIGHT &&
									heightDependentEdgeTypes.includes(option.value));

							return (
								<MenuItem key={index} value={option.value} disabled={disbaled}>
									{option.label}
								</MenuItem>
							);
						})}
				</Select>

				<Box sx={{}}>
					<FeetInchesInput
						valueInches={segment.length}
						labelInches="Length"
						onChange={handleLengthChange}
						reinitialize={reinitialize}
					/>
				</Box>
			</Box>

			<Modal
				open={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				modalTitle={
					<Typography variant="h2" fontWeight="bold">
						Confirm Deletion
					</Typography>
				}
				modalBody={
					<Typography variant="body2" color="text.secondary">
						Are you sure you want to delete Segment {segmentIndex + 1} and all
						its components?
					</Typography>
				}
				modalFooter={
					<>
						<Button
							variant="text"
							onClick={() => setShowDeleteModal(false)}
							sx={{ fontWeight: theme.typography.fontWeightBold }}
						>
							Cancel
						</Button>

						<Button
							onClick={handleDeleteSegment}
							variant="contained"
							color="error"
							sx={{ fontWeight: theme.typography.fontWeightBold }}
						>
							Delete
						</Button>
					</>
				}
				minWidth="300px"
			/>
		</Box>
	);
};

export default Segment;
